/**
 * Amount field.
 *
 * inputMode="decimal" (not type="number") is deliberate:
 *   - phones show the numeric keypad either way
 *   - type="number" adds scroll-wheel spinners that silently change a value
 *   - type="number" rejects "," on some locales, which is what a Bulgarian
 *     keyboard produces
 *
 * Validation mirrors the server exactly (see lib/format#parseAmount), so an
 * ambiguous "1.500" is caught here with an explanation rather than round
 * tripping — and, crucially, never guessed at.
 */
import { forwardRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { AmountError, currencySymbol, parseAmount, formatAmount } from '@/lib/format';
import { Input } from '@/components/ui/input';

export interface AmountInputProps {
  value: string;
  onChange: (raw: string) => void;
  /** Fires with minor units, or null while the text is not a valid amount. */
  onValueChange?: (minor: number | null) => void;
  currency: string;
  decimals?: number;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  id?: string;
  disabled?: boolean;
  /** Large presentation for the quick-add sheet's hero field. */
  size?: 'default' | 'hero';
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(function AmountInput(
  {
    value,
    onChange,
    onValueChange,
    currency,
    decimals = 2,
    placeholder = '0,00',
    autoFocus,
    className,
    id,
    disabled,
    size = 'default',
  },
  ref,
) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (value.trim() === '') {
      setError(null);
      onValueChange?.(null);
      return;
    }
    try {
      onValueChange?.(parseAmount(value, decimals));
      setError(null);
    } catch (e) {
      onValueChange?.(null);
      setError(e instanceof AmountError ? e.message : 'Невалидна сума');
    }
    // onValueChange is intentionally excluded — callers pass inline closures,
    // and including it would re-run this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals]);

  const hero = size === 'hero';

  return (
    <div className={cn('space-y-1', className)}>
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          autoComplete="off"
          // Not `off`: some Android keyboards show a full QWERTY for `off`.
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error && id ? `${id}-error` : undefined}
          className={cn(
            'tabular pr-12',
            hero && 'h-16 text-3xl font-semibold',
            error && 'border-destructive focus-visible:ring-destructive',
          )}
        />
        <span
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground',
            hero ? 'text-2xl' : 'text-sm',
          )}
        >
          {currencySymbol(currency)}
        </span>
      </div>
      {error ? (
        <p id={id ? `${id}-error` : undefined} className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
});

/** Quick amount chips — the difference between four taps and one. */
export function AmountPresets({
  presets,
  decimals = 2,
  onPick,
}: {
  presets: number[];
  decimals?: number;
  onPick: (raw: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-2">
      {presets.map((minor) => (
        <button
          key={minor}
          type="button"
          onClick={() => onPick((minor / 10 ** decimals).toFixed(decimals))}
          className="tabular rounded-full border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {formatAmount(minor, decimals)}
        </button>
      ))}
    </div>
  );
}
